import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  FileText, IndianRupee, Loader2, Mail, ArrowRight,
  CheckCircle2, Building2, Calendar, Lock, ShieldCheck
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (a) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a || 0);

export default function ClaimInvoicePage() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isClaimed, setIsClaimed] = useState(false);

  // Claim flow state
  const [step, setStep] = useState("preview"); // preview | email | otp | done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (shareToken) fetchInvoice();
  }, [shareToken]);

  const fetchInvoice = async () => {
    try {
      const res = await axios.get(`${API}/share/invoice/${shareToken}`);
      setInvoice(res.data.invoice);
      setSeller(res.data.seller);
      setIsClaimed(res.data.is_claimed);
      if (res.data.invoice?.customer_email) {
        setEmail(res.data.invoice.customer_email);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email"); return;
    }
    setRequesting(true);
    try {
      await axios.post(`${API}/share/request-otp`, { email: email.trim(), share_token: shareToken });
      toast.success("Verification code sent to your email");
      setStep("otp");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setRequesting(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) { toast.error("Enter 6-digit code"); return; }
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/share/verify-otp`, {
        email: email.trim(), otp, share_token: shareToken,
      });
      // Set auth cookies
      if (res.data.access_token) {
        document.cookie = `access_token=${res.data.access_token}; path=/; max-age=86400`;
        document.cookie = `refresh_token=${res.data.refresh_token}; path=/; max-age=2592000`;
      }
      setStep("done");
      toast.success(res.data.is_new_user ? "Account created! Invoice claimed." : "Invoice claimed!");
      setTimeout(() => {
        navigate(res.data.is_new_user ? "/company/setup" : "/dashboard");
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Invoice Not Found</h1>
          <p className="text-sm text-slate-500">This invoice link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  const inv = invoice;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EFF6FF] to-[#F9FAFB] py-8 px-4" data-testid="claim-invoice-page">
      <div className="max-w-2xl mx-auto">
        {/* Invoice Preview Card */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6" data-testid="invoice-preview-card">

          {/* Header */}
          <div className="bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {seller?.logo_url ? (
                  <img src={/^(https?:|blob:|data:)/.test(seller.logo_url) ? seller.logo_url : `${process.env.REACT_APP_BACKEND_URL}${seller.logo_url}`} alt="" className="w-10 h-10 rounded-lg bg-white/20 object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                )}
                <div>
                  <p className="text-white font-semibold text-sm">{seller?.company_name || "Business"}</p>
                  {seller?.gstin && <p className="text-white/60 text-xs font-mono">{seller.gstin}</p>}
                </div>
              </div>
              <div className="text-right">
                <p className="text-white/70 text-xs">Invoice</p>
                <p className="text-white font-bold text-sm">{inv.invoice_number}</p>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs text-slate-500 mb-0.5">Bill To</p>
                <p className="text-base font-semibold text-slate-900">{inv.customer_name}</p>
                {inv.customer_gst && <p className="text-xs text-slate-500 font-mono">{inv.customer_gst}</p>}
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <Calendar className="w-3 h-3" /> {inv.invoice_date}
                </div>
                {inv.due_date && (
                  <p className="text-xs text-slate-500">Due: {inv.due_date}</p>
                )}
              </div>
            </div>

            {/* Items */}
            <div className="border border-slate-100 rounded-lg overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="px-3 py-2 text-xs font-semibold text-slate-500">Item</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-500 text-right">Qty</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-500 text-right">Rate</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-500 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {(inv.items || []).map((item, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2.5 text-slate-700">{item.name || item.description}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-right">{item.quantity}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-right">{fmt(item.unit_price)}</td>
                      <td className="px-3 py-2.5 text-slate-900 font-medium text-right">{fmt((item.quantity || 0) * (item.unit_price || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-1.5">
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Subtotal</span>
                  <span>{fmt(inv.subtotal)}</span>
                </div>
                {inv.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Discount</span>
                    <span>-{fmt(inv.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-slate-600">
                  <span>GST</span>
                  <span>{fmt(inv.tax_amount)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-slate-900 pt-1.5 border-t border-slate-200">
                  <span>Total</span>
                  <span>{fmt(inv.grand_total)}</span>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="mt-4 flex items-center gap-2">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                inv.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" :
                inv.payment_status === "partial" ? "bg-amber-50 text-amber-700" :
                "bg-red-50 text-red-700"
              }`}>{inv.payment_status}</span>
            </div>
          </div>
        </motion.div>

        {/* Claim CTA */}
        {!isClaimed && step === "preview" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-6 text-center" data-testid="claim-cta">
            <ShieldCheck className="w-10 h-10 text-[#2563EB] mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-900 mb-1">This invoice was sent to you</h2>
            <p className="text-sm text-slate-500 mb-4">Claim it to add it to your account for tracking and records.</p>
            <Button onClick={() => setStep("email")} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6" data-testid="claim-invoice-btn">
              Claim This Invoice <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </motion.div>
        )}

        {/* Email Step */}
        <AnimatePresence>
          {step === "email" && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-white rounded-2xl shadow-lg p-6" data-testid="claim-email-step">
              <h2 className="text-base font-bold text-slate-900 mb-1">Verify your identity</h2>
              <p className="text-sm text-slate-500 mb-4">We'll send a one-time code to your email.</p>
              <div className="relative mb-3">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="pl-9" data-testid="claim-email-input" />
              </div>
              <Button onClick={handleRequestOTP} disabled={requesting} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="claim-send-otp-btn">
                {requesting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Lock className="w-4 h-4 mr-1.5" />}
                Send Verification Code
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* OTP Step */}
        <AnimatePresence>
          {step === "otp" && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-white rounded-2xl shadow-lg p-6" data-testid="claim-otp-step">
              <h2 className="text-base font-bold text-slate-900 mb-1">Enter verification code</h2>
              <p className="text-sm text-slate-500 mb-4">Sent to {email}</p>
              <Input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000"
                className="text-center text-2xl tracking-[0.5em] font-mono mb-3" maxLength={6} data-testid="claim-otp-input" />
              <Button onClick={handleVerifyOTP} disabled={verifying || otp.length !== 6} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="claim-verify-btn">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                Verify & Claim Invoice
              </Button>
              <button onClick={() => setStep("email")} className="w-full mt-2 text-sm text-slate-500 hover:text-slate-700">
                Use different email
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Done */}
        {step === "done" && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-lg p-6 text-center" data-testid="claim-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-900 mb-1">Invoice Claimed!</h2>
            <p className="text-sm text-slate-500">Redirecting you to your dashboard...</p>
          </motion.div>
        )}

        {/* Already claimed */}
        {isClaimed && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-center" data-testid="already-claimed">
            <p className="text-sm font-medium text-amber-800">This invoice has already been claimed.</p>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by InvoiceSetu — Secure invoice management
        </p>
      </div>
    </div>
  );
}
